import { InputCoverageCallout } from '@/components/Experiments/InputCoverageCallout';
import { requireOrdinaryEntityType } from '@einhaus/project314-shared';
import type { ExperimentOptionComparisonEvidence } from '@einhaus/project314-types';
import { OptionOutcomeCompletion, formatOptionOutcomeCompletion } from '@/components/Experiments/OptionOutcomeCompletion';
import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import UserLayout from '@/components/Layout/UserLayout.tsx';
import DataTable from '@/components/UI/DataTable';
import { showSpinner, hideSpinner } from '@/components/Layout/LoadingSpinner';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner.tsx';
import { trpc } from '@/api/trpc.ts';
import { useQuery } from '@tanstack/react-query';
import { useAppQuery } from '@/hooks/useAppQuery';
import { DEFAULT_REQUISITIES_STALE_TIME } from '@/config/constants';
import { Button, Card, Badge, EmptyState, FormSelect, StatusBadge } from '@/components/UI';
import { FaArrowLeft, FaDownload, FaChartBar } from 'react-icons/fa';
import dayjs from 'dayjs';
import ParameterDiscovery from '@/components/Experiments/ParameterDiscovery';
import ParameterDiscoveryAuditPanel from '@/components/Experiments/ParameterDiscoveryAuditPanel';
import { computeEdgeEligibleCount, EdgeCoverageCell } from '@/components/Experiments/EdgeCoverageCell';
import { ScreenerOptimizerPanel } from '@/components/Experiments/ScreenerOptimizerPanel';
import PrimaryObjectivePerformanceCard from '@/components/Experiments/PrimaryObjectivePerformanceCard';
import { RegimeProfilePanel } from '@/components/Experiments/regimeProfile/RegimeProfilePanel';
import { ResearchWorkspaceCard } from '@/components/Experiments/ResearchWorkspaceCard';
import { showToast } from '@/components/Layout/Toast';
import type { RealizedOutcomeMaterializationEntityType } from '@/components/Experiments/parameterDiscovery/ObjectiveModeControl';
import type { ColumnDef } from '@/components/UI/dataTableConfig';
import { formatTimeHorizon, getEventHorizonTooltip, isEventHorizon } from '@/utils/timeHorizonGenerator';
import { useAuth } from '@/context/AuthContext';
import { getCatalogAuditPresentation } from './catalogAuditPresentation';

interface DurationTableRow {
    horizonSeconds: number;
    horizonLabel: string;
    sourceEntryCount: number;
    metrics: {
        avgProfitPercent: number | null;
        medianProfitPercent: number | null;
        avgSpyProfitPercent: number | null;
        avgAlphaPercent: number | null;
        medianAlphaPercent: number | null;
        avgEntryEdgePercent?: number | null;
        entryEdgeCoveredCount?: number;
        entryEdgeCoveredEmptyCount?: number;
        entryEdgeMissingCount?: number;
        winRate: number | null;
        winRateVsSpy: number | null;
        avgMaxProfitPercent: number | null;
        avgMaxLossPercent: number | null;
        profitableEntries: number;
        totalEntries: number;
        stdDevProfitPercent: number | null;
        profitFactor: number | null;
        coefficientOfVariation: number | null;
        // Option-specific metrics
        optionAvgProfitPercent?: number | null;
        avgOptionEntryEdgePercent?: number | null;
        optionEntryEdgeCoveredCount?: number;
        optionEntryEdgeCoveredEmptyCount?: number;
        optionEntryEdgeMissingCount?: number;
        optionWinRate?: number | null;
        optionAvgMaxProfitPercent?: number | null;
        optionAvgMaxLossPercent?: number | null;
        optionProfitableEntries?: number;
        optionTotalEntries?: number;
        medianOptionProfitPercent?: number | null;
        medianOptionMaxProfitPercent?: number | null;
        optionOutperformanceRate?: number | null;
        optionAvgLeverage?: number | null;
        optionSuccessRate?: number | null;
        optionEvidence?: ExperimentOptionComparisonEvidence;
    };
}

function hasActiveExperimentLifecycle(executionStatus: string, discoveryStatus: string): boolean {
    return (
        executionStatus === 'queued' ||
        executionStatus === 'running' ||
        discoveryStatus === 'preparing_objective' ||
        discoveryStatus === 'pending' ||
        discoveryStatus === 'running'
    );
}

type ExperimentResultsView = 'results' | 'regime' | 'discovery' | 'scorecards' | 'optimizer' | 'entryOptimizer' | 'admin';
type ParameterDiscoveryMetric = 'stock' | 'options';
type DurationColumnPreset = 'core' | 'stock' | 'options' | 'risk' | 'all';

// Tab order is workflow order: the regime profile is the first stop in the research workspace, before
// discovery/scorecards/optimization bake the regime mix into their optima.
const experimentViewOptions: { id: ExperimentResultsView; label: string; adminOnly?: boolean }[] = [
    { id: 'results', label: 'Results' },
    { id: 'regime', label: 'Regime' },
    { id: 'discovery', label: 'Parameter Discovery' },
    { id: 'scorecards', label: 'Scorecards' },
    { id: 'optimizer', label: 'Screener Optimizer' },
    { id: 'entryOptimizer', label: 'Entry Optimizer' },
    { id: 'admin', label: 'Admin', adminOnly: true }
];

const durationColumnPresetOptions: { value: DurationColumnPreset; label: string }[] = [
    { value: 'core', label: 'Core' },
    { value: 'stock', label: 'Stock' },
    { value: 'options', label: 'Options' },
    { value: 'risk', label: 'Risk' },
    { value: 'all', label: 'All' }
];

const parseExperimentView = (value: string | null): ExperimentResultsView => {
    if (value === 'parameterDiscovery') return 'discovery';
    if (
        value === 'regime' ||
        value === 'discovery' ||
        value === 'scorecards' ||
        value === 'optimizer' ||
        value === 'entryOptimizer' ||
        value === 'admin'
    )
        return value;
    return 'results';
};

const parsePositiveInteger = (value: string | null): number | null => {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const parseParameterDiscoveryMetric = (value: string | null): ParameterDiscoveryMetric | null => {
    if (value === 'stock' || value === 'options') return value;
    return null;
};

const ExperimentResults: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { sessionPolicy } = useAuth();
    const { id } = useParams<{ id: string }>();
    const experimentId = parseInt(id || '0');

    const strategyIdFromQuery = Number(searchParams.get('strategyId'));
    const activeView = parseExperimentView(searchParams.get('view'));
    const requestedWorkbenchMetric = parseParameterDiscoveryMetric(searchParams.get('metric'));

    const backToExperimentsUrl =
        !Number.isNaN(strategyIdFromQuery) && strategyIdFromQuery > 0
            ? `/user/experiments?strategyId=${strategyIdFromQuery}`
            : '/user/experiments';

    const compareExperimentsUrl =
        !Number.isNaN(strategyIdFromQuery) && strategyIdFromQuery > 0
            ? `/user/experimentCompare?strategyId=${strategyIdFromQuery}`
            : '/user/experimentCompare';

    const preservedStrategyQueryPart =
        !Number.isNaN(strategyIdFromQuery) && strategyIdFromQuery > 0 ? `&strategyId=${strategyIdFromQuery}` : '';

    const setExperimentView = useCallback(
        (view: ExperimentResultsView, options?: { replace?: boolean }) => {
            const next = new URLSearchParams(searchParams);

            if (view === 'results') {
                next.delete('view');
            } else {
                next.set('view', view);
            }

            setSearchParams(next, { replace: options?.replace ?? false });
        },
        [searchParams, setSearchParams]
    );

    const updateWorkbenchScope = useCallback(
        (scope: { duration?: string; horizonSeconds?: number; metric?: ParameterDiscoveryMetric }, options?: { replace?: boolean }) => {
            const next = new URLSearchParams(searchParams);

            if (scope.duration) next.set('duration', scope.duration);
            if (scope.horizonSeconds) next.set('horizonSeconds', String(scope.horizonSeconds));
            if (scope.metric) next.set('metric', scope.metric);

            setSearchParams(next, { replace: options?.replace ?? true });
        },
        [searchParams, setSearchParams]
    );

    const [error, setError] = useState<string | null>(null);
    const [durationColumnPreset, setDurationColumnPreset] = useState<DurationColumnPreset>('core');
    const previousActiveDiscoveryJobsRef = useRef(0);
    const previousCatalogAuditStatusRef = useRef<'not_ready' | 'pending' | 'ready' | null>(null);

    // Fetch experiment results (includes status)
    const resultsQuery = useAppQuery(
        {
            ...trpc.experiment.results.queryOptions({ experimentId }),
            staleTime: DEFAULT_REQUISITIES_STALE_TIME,
            refetchInterval: (query) => {
                const { data } = query.state;
                const experiment = data?.experiment;
                return experiment && hasActiveExperimentLifecycle(experiment.executionStatus, experiment.discoveryStatus) ? 5000 : false;
            }
        },
        {
            fallbackErrorMessage: 'Failed to load results',
            setError: (msg) => setError(msg)
        }
    );

    // Fetch parameter discovery jobs
    const discoveryJobsQuery = useQuery({
        ...trpc.parameterDiscovery.getParameterDiscoveryJobs.queryOptions({
            experimentId
        }),
        refetchInterval: (query) => {
            const jobs = query.state.data?.jobs ?? [];
            const activeJobs = jobs.filter((job) => job.status === 'pending' || job.status === 'running').length;
            const catalogAuditPending = query.state.data?.catalogAudit.status === 'pending';

            return activeJobs > 0 || catalogAuditPending ? 5000 : false;
        },
        retry: false
    });

    const discoveryJobCount = useMemo(() => {
        const jobs = discoveryJobsQuery.data?.jobs ?? [];
        const activeJobs = jobs.filter((job) => job.status === 'pending' || job.status === 'running').length;

        return { active: activeJobs, total: jobs.length };
    }, [discoveryJobsQuery.data?.jobs]);

    const catalogAuditPresentation = getCatalogAuditPresentation(discoveryJobsQuery.data?.catalogAudit);

    // Respond to discovery job status transitions.
    useEffect(() => {
        const jobs = discoveryJobsQuery.data?.jobs;

        if (!jobs) return;

        const activeJobs = jobs.filter((job) => job.status === 'pending' || job.status === 'running').length;
        const prevActiveJobs = previousActiveDiscoveryJobsRef.current;

        previousActiveDiscoveryJobsRef.current = activeJobs;

        if (activeJobs > 0 && activeView === 'results') {
            setExperimentView('discovery', { replace: true });
        }

        // Show toast when all jobs complete
        if (prevActiveJobs > 0 && activeJobs === 0 && jobs.length > 0) {
            const completedJobs = jobs.filter((job) => job.status === 'completed').length;
            const failedJobs = jobs.filter((job) => job.status === 'failed').length;

            if (failedJobs > 0) {
                showToast(`Parameter discovery completed with ${failedJobs} failed jobs`, 'warning');
            } else if (completedJobs === jobs.length) {
                const auditPending = discoveryJobsQuery.data?.catalogAudit.status === 'pending';
                showToast(
                    auditPending
                        ? 'Parameter discovery completed; finalizing scorecard and optimization insights in the background.'
                        : 'All parameter discovery jobs and insight finalization completed successfully!',
                    auditPending ? 'info' : 'success'
                );
            }
        }
    }, [activeView, discoveryJobsQuery.data?.catalogAudit.status, discoveryJobsQuery.data?.jobs, setExperimentView]);

    useEffect(() => {
        const status = discoveryJobsQuery.data?.catalogAudit.status;
        if (!status) return;

        const previousStatus = previousCatalogAuditStatusRef.current;
        previousCatalogAuditStatusRef.current = status;

        if (previousStatus === 'pending' && status === 'ready') {
            showToast('Parameter Discovery scorecard and optimization insights are ready.', 'success');
        }
    }, [discoveryJobsQuery.data?.catalogAudit.status]);

    const strategyId = resultsQuery.data?.experiment?.strategyId ?? null;

    // Fetch strategy information
    const strategyQuery = useAppQuery({
        ...trpc.strategy.fetchStrategies.queryOptions({
            ids: strategyId ? [strategyId] : [],
            requireActive: false
        }),
        enabled: !!strategyId,
        staleTime: DEFAULT_REQUISITIES_STALE_TIME
    });

    // Format profit/loss with color
    const ProfitCell: React.FC<{ value: number | null | undefined }> = ({ value }) => {
        if (value === null || value === undefined) return <span className="text-zinc-500">-</span>;
        const color = value >= 0 ? 'text-green-400' : 'text-red-400';
        return <span className={color}>{value.toFixed(2)}%</span>;
    };

    // Build columns for duration detail table
    const buildDurationColumns = (
        includeOptions: boolean = false,
        columnPreset: DurationColumnPreset = durationColumnPreset
    ): ColumnDef<DurationTableRow>[] => {
        const baseColumns: ColumnDef<DurationTableRow>[] = [
            {
                accessorKey: 'horizonLabel',
                header: 'Time Horizon',
                minSize: 100,
                size: 120,
                maxSize: 150,
                cell: ({ row }) => {
                    const seconds = row.original.horizonSeconds;
                    const label = row.original.horizonLabel;
                    const tooltip = isEventHorizon(seconds) ? getEventHorizonTooltip(seconds) : null;

                    if (tooltip) {
                        return (
                            <span className="font-medium text-white" title={tooltip}>
                                {label}
                            </span>
                        );
                    }

                    return <span className="font-medium text-white">{label}</span>;
                }
            },
            {
                accessorKey: 'metrics.avgProfitPercent',
                header: 'Horizon Avg %',
                minSize: 100,
                size: 120,
                maxSize: 140,
                cell: ({ row }) => <ProfitCell value={row.original.metrics.avgProfitPercent} />
            },
            {
                accessorKey: 'metrics.medianProfitPercent',
                header: 'Horizon Median %',
                minSize: 100,
                size: 120,
                maxSize: 140,
                cell: ({ row }) => <ProfitCell value={row.original.metrics.medianProfitPercent} />
            },
            {
                accessorKey: 'metrics.avgSpyProfitPercent',
                header: 'SPY %',
                minSize: 80,
                size: 100,
                maxSize: 120,
                cell: ({ row }) => <ProfitCell value={row.original.metrics.avgSpyProfitPercent} />
            },
            {
                accessorKey: 'metrics.avgAlphaPercent',
                header: 'Alpha %',
                minSize: 80,
                size: 100,
                maxSize: 120,
                cell: ({ row }) => <ProfitCell value={row.original.metrics.avgAlphaPercent} />
            },
            {
                accessorKey: 'metrics.medianAlphaPercent',
                header: 'Median Alpha %',
                minSize: 80,
                size: 100,
                maxSize: 120,
                cell: ({ row }) => <ProfitCell value={row.original.metrics.medianAlphaPercent} />
            },
            {
                accessorKey: 'metrics.avgEntryEdgePercent',
                header: () => (
                    <span title="vs random entry into the same name, month, and fixed horizon — shown as avg (covered/eligible); dynamic-exit outcomes use the nearest fixed-horizon proxy, not a replay of the exit logic">
                        Avg Edge %
                    </span>
                ),
                minSize: 100,
                size: 120,
                maxSize: 150,
                cell: ({ row }) => (
                    <EdgeCoverageCell
                        coveredCount={row.original.metrics.entryEdgeCoveredCount}
                        coveredEmptyCount={row.original.metrics.entryEdgeCoveredEmptyCount}
                        missingCount={row.original.metrics.entryEdgeMissingCount}
                        value={row.original.metrics.avgEntryEdgePercent}
                    />
                )
            },
            {
                accessorKey: 'metrics.winRate',
                header: 'Win Rate',
                minSize: 80,
                size: 100,
                maxSize: 120,
                cell: ({ row }) => {
                    const { winRate } = row.original.metrics;
                    if (winRate === null || winRate === undefined) return <span className="text-zinc-500">-</span>;
                    return <span className="text-white">{winRate.toFixed(1)}%</span>;
                }
            },
            {
                accessorKey: 'metrics.winRateVsSpy',
                header: 'Beat SPY %',
                minSize: 90,
                size: 110,
                maxSize: 130,
                cell: ({ row }) => {
                    const { winRateVsSpy } = row.original.metrics;
                    if (winRateVsSpy === null || winRateVsSpy === undefined) return <span className="text-zinc-500">-</span>;
                    const color = winRateVsSpy >= 50 ? 'text-green-400' : 'text-red-400';
                    return <span className={color}>{winRateVsSpy.toFixed(1)}%</span>;
                }
            },
            {
                accessorKey: 'metrics.totalEntries',
                header: 'Horizon Results',
                minSize: 100,
                size: 120,
                maxSize: 140,
                cell: ({ row }) => (
                    <span className="text-white">
                        {row.original.metrics.totalEntries}/{row.original.sourceEntryCount}
                    </span>
                )
            },
            {
                accessorKey: 'metrics.profitableEntries',
                header: 'Profitable',
                minSize: 90,
                size: 110,
                maxSize: 130,
                cell: ({ row }) => <span className="text-green-400">{row.original.metrics.profitableEntries}</span>
            },
            {
                accessorKey: 'metrics.avgMaxProfitPercent',
                header: 'Avg Max Profit',
                minSize: 120,
                size: 140,
                maxSize: 160,
                cell: ({ row }) => <ProfitCell value={row.original.metrics.avgMaxProfitPercent} />
            },
            {
                accessorKey: 'metrics.avgMaxLossPercent',
                header: 'Avg Max Loss',
                minSize: 120,
                size: 140,
                maxSize: 160,
                cell: ({ row }) => <ProfitCell value={row.original.metrics.avgMaxLossPercent} />
            },
            {
                accessorKey: 'metrics.profitFactor',
                header: 'Profit Factor',
                minSize: 90,
                size: 110,
                maxSize: 130,
                cell: ({ row }) => {
                    const { profitFactor } = row.original.metrics;
                    if (profitFactor === null || profitFactor === undefined) return <span className="text-zinc-500">-</span>;
                    const color = profitFactor >= 1.5 ? 'text-green-400' : profitFactor >= 1.0 ? 'text-yellow-400' : 'text-red-400';
                    return <span className={color}>{profitFactor.toFixed(2)}</span>;
                }
            },
            {
                accessorKey: 'metrics.coefficientOfVariation',
                header: 'CV',
                minSize: 60,
                size: 80,
                maxSize: 100,
                cell: ({ row }) => {
                    const { coefficientOfVariation } = row.original.metrics;
                    if (coefficientOfVariation === null || coefficientOfVariation === undefined)
                        return <span className="text-zinc-500">-</span>;

                    // Lower CV is better (more consistent)
                    const color =
                        coefficientOfVariation <= 1.0
                            ? 'text-green-400'
                            : coefficientOfVariation <= 2.0
                              ? 'text-yellow-400'
                              : 'text-red-400';

                    return <span className={color}>{coefficientOfVariation.toFixed(2)}</span>;
                }
            }
        ];

        // Add option columns if experiment includes options
        if (includeOptions) {
            baseColumns.push(
                {
                    accessorKey: 'metrics.optionTotalEntries',
                    header: 'Completed / Attempts',
                    minSize: 110,
                    size: 130,
                    maxSize: 150,
                    cell: ({ row }) => <OptionOutcomeCompletion evidence={row.original.metrics.optionEvidence} />
                },
                {
                    accessorKey: 'metrics.optionAvgProfitPercent',
                    header: 'Horizon Option Avg %',
                    minSize: 110,
                    size: 130,
                    maxSize: 150,
                    cell: ({ row }) => <ProfitCell value={row.original.metrics.optionAvgProfitPercent} />
                },
                {
                    accessorKey: 'metrics.avgOptionEntryEdgePercent',
                    header: () => (
                        <span title="option profit vs random entries into the same contract archetype, name, month, and fixed horizon — shown as avg (covered/eligible); dynamic-exit outcomes use the nearest fixed-horizon proxy">
                            Avg Opt Edge %
                        </span>
                    ),
                    minSize: 110,
                    size: 130,
                    maxSize: 160,
                    cell: ({ row }) => (
                        <EdgeCoverageCell
                            coveredCount={row.original.metrics.optionEntryEdgeCoveredCount}
                            coveredEmptyCount={row.original.metrics.optionEntryEdgeCoveredEmptyCount}
                            missingCount={row.original.metrics.optionEntryEdgeMissingCount}
                            value={row.original.metrics.avgOptionEntryEdgePercent}
                        />
                    )
                },
                {
                    accessorKey: 'metrics.optionAvgMaxProfitPercent',
                    header: 'Option Avg Max %',
                    minSize: 120,
                    size: 140,
                    maxSize: 160,
                    cell: ({ row }) => {
                        const value = row.original.metrics.optionAvgMaxProfitPercent;
                        if (value === null || value === undefined) return <span className="text-zinc-500">-</span>;
                        const color = value >= 0 ? 'text-green-400' : 'text-red-400';
                        return <span className={color}>{value.toFixed(2)}%</span>;
                    }
                },
                {
                    accessorKey: 'metrics.medianOptionProfitPercent',
                    header: 'Option Median %',
                    minSize: 110,
                    size: 130,
                    maxSize: 150,
                    cell: ({ row }) => <ProfitCell value={row.original.metrics.medianOptionProfitPercent} />
                },
                {
                    accessorKey: 'metrics.medianOptionMaxProfitPercent',
                    header: 'Option Max Median %',
                    minSize: 130,
                    size: 150,
                    maxSize: 170,
                    cell: ({ row }) => {
                        const value = row.original.metrics.medianOptionMaxProfitPercent;
                        if (value === null || value === undefined) return <span className="text-zinc-500">-</span>;
                        const color = value >= 0 ? 'text-green-400' : 'text-red-400';
                        return <span className={color}>{value.toFixed(2)}%</span>;
                    }
                },
                {
                    accessorKey: 'metrics.optionWinRate',
                    header: 'Option Win %',
                    minSize: 100,
                    size: 120,
                    maxSize: 140,
                    cell: ({ row }) => {
                        const { optionWinRate } = row.original.metrics;
                        if (optionWinRate === null || optionWinRate === undefined) return <span className="text-zinc-500">-</span>;
                        return <span className="text-white">{optionWinRate.toFixed(1)}%</span>;
                    }
                },
                {
                    accessorKey: 'metrics.optionOutperformanceRate',
                    header: 'Beat Stock %',
                    minSize: 100,
                    size: 120,
                    maxSize: 140,
                    cell: ({ row }) => {
                        const { optionOutperformanceRate } = row.original.metrics;
                        if (optionOutperformanceRate === null || optionOutperformanceRate === undefined)
                            return <span className="text-zinc-500">-</span>;
                        const color = optionOutperformanceRate >= 50 ? 'text-green-400' : 'text-red-400';
                        return <span className={color}>{optionOutperformanceRate.toFixed(1)}%</span>;
                    }
                },
                {
                    accessorKey: 'metrics.optionAvgLeverage',
                    header: 'Avg Leverage',
                    minSize: 100,
                    size: 120,
                    maxSize: 140,
                    cell: ({ row }) => {
                        const { optionAvgLeverage } = row.original.metrics;
                        if (optionAvgLeverage === null || optionAvgLeverage === undefined) return <span className="text-zinc-500">-</span>;
                        return <span className="text-white">{optionAvgLeverage.toFixed(2)}x</span>;
                    }
                }
            );
        }

        if (columnPreset === 'all') return baseColumns;

        const coreColumns = new Set([
            'horizonLabel',
            'metrics.avgProfitPercent',
            'metrics.medianProfitPercent',
            'metrics.winRate',
            'metrics.totalEntries',
            'metrics.optionAvgProfitPercent',
            'metrics.optionWinRate'
        ]);

        const stockColumns = new Set([
            'horizonLabel',
            'metrics.avgProfitPercent',
            'metrics.medianProfitPercent',
            'metrics.avgSpyProfitPercent',
            'metrics.avgAlphaPercent',
            'metrics.medianAlphaPercent',
            'metrics.avgEntryEdgePercent',
            'metrics.winRate',
            'metrics.winRateVsSpy',
            'metrics.totalEntries',
            'metrics.profitableEntries'
        ]);

        const optionsColumns = new Set([
            'horizonLabel',
            'metrics.optionTotalEntries',
            'metrics.optionAvgProfitPercent',
            'metrics.avgOptionEntryEdgePercent',
            'metrics.optionAvgMaxProfitPercent',
            'metrics.medianOptionProfitPercent',
            'metrics.medianOptionMaxProfitPercent',
            'metrics.optionWinRate',
            'metrics.optionOutperformanceRate',
            'metrics.optionAvgLeverage'
        ]);

        const riskColumns = new Set([
            'horizonLabel',
            'metrics.avgMaxProfitPercent',
            'metrics.avgMaxLossPercent',
            'metrics.profitFactor',
            'metrics.coefficientOfVariation',
            'metrics.optionAvgMaxProfitPercent',
            'metrics.medianOptionMaxProfitPercent',
            'metrics.optionAvgLeverage'
        ]);

        const allowedColumns =
            columnPreset === 'stock'
                ? stockColumns
                : columnPreset === 'options'
                  ? optionsColumns
                  : columnPreset === 'risk'
                    ? riskColumns
                    : coreColumns;

        return baseColumns.filter((column) => {
            const accessorKey = 'accessorKey' in column ? column.accessorKey : undefined;
            return typeof accessorKey === 'string' && allowedColumns.has(accessorKey);
        });
    };

    // Extract data
    const experiment = resultsQuery.data?.experiment;
    const primaryObjective = resultsQuery.data?.primaryObjective;
    const durations = resultsQuery.data?.durations || [];
    const defaultWorkbenchMetric: ParameterDiscoveryMetric = experiment?.includeOptions ? 'options' : 'stock';
    const selectedWorkbenchMetric = requestedWorkbenchMetric ?? defaultWorkbenchMetric;

    const realizedOutcomeMaterializationEntityType = useMemo<RealizedOutcomeMaterializationEntityType | null>(() => {
        const supportedTypes = new Set<RealizedOutcomeMaterializationEntityType>();

        for (const entity of experiment?.entities ?? []) {
            if (entity.entityType === 'Asset' || entity.entityType === 'AssetIndex' || entity.entityType === 'Cryptocurrency') {
                supportedTypes.add(entity.entityType);
            }
        }

        if (supportedTypes.size !== 1) return null;

        return Array.from(supportedTypes)[0] ?? null;
    }, [experiment?.entities]);

    const defaultWorkbenchScope = useMemo(() => {
        const completedJobs = (discoveryJobsQuery.data?.jobs ?? [])
            .filter((job) => job.status === 'completed')
            .slice()
            .sort((a, b) => dayjs(b.completedAt ?? b.createdAt).valueOf() - dayjs(a.completedAt ?? a.createdAt).valueOf());

        const latestJob = completedJobs[0];
        const firstDuration = durations[0];

        return {
            duration: latestJob?.duration ?? firstDuration?.duration ?? '',
            horizonSeconds: latestJob?.horizonSeconds ?? firstDuration?.horizons?.[0] ?? 0
        };
    }, [discoveryJobsQuery.data?.jobs, durations]);

    const requestedWorkbenchDuration = searchParams.get('duration');

    const requestedWorkbenchHorizon =
        parsePositiveInteger(searchParams.get('horizonSeconds')) ?? parsePositiveInteger(searchParams.get('horizon'));

    const selectedWorkbenchDuration = requestedWorkbenchDuration || defaultWorkbenchScope.duration;

    const selectedWorkbenchHorizon = requestedWorkbenchHorizon ?? defaultWorkbenchScope.horizonSeconds;

    const canAccessAdminWorkbenches = sessionPolicy.canAccessAdminRoutes;
    const renderedView = activeView === 'admin' && !canAccessAdminWorkbenches ? 'results' : activeView;
    const hasRequestedWorkbenchScope = Boolean(requestedWorkbenchDuration && requestedWorkbenchHorizon);

    const isResolvingWorkbenchScope =
        renderedView !== 'results' &&
        renderedView !== 'regime' &&
        !hasRequestedWorkbenchScope &&
        discoveryJobsQuery.isLoading &&
        discoveryJobsQuery.fetchStatus !== 'idle';

    const handleDiscoveryScopeChange = useCallback(
        (scope: { duration: string; horizonSeconds: number; metric: ParameterDiscoveryMetric }) => {
            updateWorkbenchScope(scope, { replace: true });
        },
        [updateWorkbenchScope]
    );

    const handleWorkbenchMetricChange = useCallback(
        (metric: ParameterDiscoveryMetric) => {
            updateWorkbenchScope({ metric }, { replace: true });
        },
        [updateWorkbenchScope]
    );

    useEffect(() => {
        if (!experiment || requestedWorkbenchMetric !== null) return;
        if (renderedView !== 'discovery' && renderedView !== 'scorecards' && renderedView !== 'admin') return;

        updateWorkbenchScope({ metric: defaultWorkbenchMetric }, { replace: true });
    }, [defaultWorkbenchMetric, experiment, renderedView, requestedWorkbenchMetric, updateWorkbenchScope]);

    const { isLoading } = resultsQuery;

    // Loading spinner
    useEffect(() => {
        if (isLoading) {
            showSpinner();
        } else {
            hideSpinner();
        }

        return () => hideSpinner();
    }, [isLoading]);

    useEffect(() => {
        const { documentElement, body } = document;
        const previousDocumentOverflowX = documentElement.style.overflowX;
        const previousBodyOverflowX = body.style.overflowX;

        documentElement.style.overflowX = 'hidden';
        body.style.overflowX = 'hidden';

        return () => {
            documentElement.style.overflowX = previousDocumentOverflowX;
            body.style.overflowX = previousBodyOverflowX;
        };
    }, []);

    // Export to CSV
    const handleExportCSV = () => {
        if (durations.length === 0) return;

        const rows: string[][] = [];

        const includeOptions = experiment?.includeOptions || false;

        const headers = [
            'Duration',
            'Time Horizon',
            'Fixed-Horizon Avg Profit %',
            'Fixed-Horizon Median Profit %',
            'SPY %',
            'Alpha %',
            'Avg Edge %',
            'Avg Edge Covered',
            'Avg Edge Eligible',
            'Win Rate %',
            'Beat SPY %',
            'Source Entries',
            'Fixed-Horizon Results',
            'Profitable Entries',
            'Avg Max Profit %',
            'Avg Max Loss %'
        ];

        // Add option headers if experiment includes options
        if (includeOptions) {
            headers.push(
                'Completed / Attempts',
                'Fixed-Horizon Option Avg %',
                'Fixed-Horizon Option Median %',
                'Avg Opt Edge %',
                'Avg Opt Edge Covered',
                'Avg Opt Edge Eligible',
                'Option Win %',
                'Option Beat Stock %',
                'Option Avg Leverage'
            );
        }

        rows.push(headers);

        durations.forEach((durationResult) => {
            durationResult.horizons.forEach((horizon) => {
                const metrics = durationResult.metrics[String(horizon)];

                if (metrics) {
                    const row = [
                        durationResult.duration,
                        formatTimeHorizon(horizon),
                        metrics.avgProfitPercent?.toFixed(2) || '',
                        metrics.medianProfitPercent?.toFixed(2) || '',
                        metrics.avgSpyProfitPercent?.toFixed(2) || '',
                        metrics.avgAlphaPercent?.toFixed(2) || '',
                        metrics.avgEntryEdgePercent?.toFixed(2) || '',
                        String(metrics.entryEdgeCoveredCount ?? 0),
                        String(
                            computeEdgeEligibleCount({
                                coveredCount: metrics.entryEdgeCoveredCount,
                                coveredEmptyCount: metrics.entryEdgeCoveredEmptyCount,
                                missingCount: metrics.entryEdgeMissingCount
                            })
                        ),
                        metrics.winRate?.toFixed(1) || '',
                        metrics.winRateVsSpy?.toFixed(1) || '',
                        durationResult.totalEntries.toString(),
                        metrics.totalEntries.toString(),
                        metrics.profitableEntries.toString(),
                        metrics.avgMaxProfitPercent?.toFixed(2) || '',
                        metrics.avgMaxLossPercent?.toFixed(2) || ''
                    ];

                    // Add option data if experiment includes options
                    if (includeOptions) {
                        const optionEntriesStr = formatOptionOutcomeCompletion(metrics.optionEvidence);

                        row.push(
                            optionEntriesStr,
                            metrics.optionAvgProfitPercent?.toFixed(2) || '',
                            metrics.medianOptionProfitPercent?.toFixed(2) || '',
                            metrics.avgOptionEntryEdgePercent?.toFixed(2) || '',
                            String(metrics.optionEntryEdgeCoveredCount ?? 0),
                            String(
                                computeEdgeEligibleCount({
                                    coveredCount: metrics.optionEntryEdgeCoveredCount,
                                    coveredEmptyCount: metrics.optionEntryEdgeCoveredEmptyCount,
                                    missingCount: metrics.optionEntryEdgeMissingCount
                                })
                            ),
                            metrics.optionWinRate?.toFixed(1) || '',
                            metrics.optionOutperformanceRate?.toFixed(1) || '',
                            metrics.optionAvgLeverage?.toFixed(2) || ''
                        );
                    }

                    rows.push(row);
                }
            });
        });

        const csv = rows.map((row) => row.join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `experiment-${experimentId}-fixed-horizon-results.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    if (isLoading) {
        return (
            <UserLayout title="Experiment Results - Project 314">
                <DataLoadingSpinner text="Loading experiment results..." />
            </UserLayout>
        );
    }

    if (error) {
        return (
            <UserLayout title="Experiment Results - Error - Project 314">
                <div className="container mx-auto">
                    <div className="bg-red-100 text-red-800 border border-red-200 p-4 mb-4 rounded-md">{error}</div>
                </div>
            </UserLayout>
        );
    }

    if (!experiment) {
        return (
            <UserLayout title="Experiment Not Found - Project 314">
                <div className="container mx-auto">
                    <div className="text-center py-12">
                        <h3 className="text-white text-xl mb-4">Experiment not found</h3>
                        <Button variant="secondary" onClick={() => navigate(backToExperimentsUrl)}>
                            Back to Experiments
                        </Button>
                    </div>
                </div>
            </UserLayout>
        );
    }

    return (
        <UserLayout
            containerSize={renderedView === 'results' ? '1400px' : '2200px'}
            title={`Experiment Results - ${experiment.name} - Project 314`}
        >
            <div className="container mx-auto overflow-x-hidden px-4">
                {/* Header */}
                <div className="flex flex-col justify-between gap-4 mb-6 xl:flex-row xl:items-start">
                    <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                            <Button
                                className="inline-flex items-center whitespace-nowrap"
                                size="sm"
                                variant="secondary"
                                onClick={() => navigate(backToExperimentsUrl)}
                            >
                                <FaArrowLeft className="mr-2" />
                                Back
                            </Button>
                            <h1 className="text-white text-xl font-semibold">{experiment.name}</h1>
                        </div>
                        <div className="flex flex-wrap items-center gap-4 mt-2">
                            <StatusBadge status={experiment.executionStatus}>Execution: {experiment.executionStatus}</StatusBadge>
                            {experiment.discoveryStatus !== 'disabled' && (
                                <StatusBadge status={experiment.discoveryStatus}>Discovery: {experiment.discoveryStatus}</StatusBadge>
                            )}
                            {catalogAuditPresentation && (
                                <span aria-live="polite" role="status" title={catalogAuditPresentation.title}>
                                    <StatusBadge
                                        pulse={catalogAuditPresentation.pulse}
                                        status={catalogAuditPresentation.badgeStatus}
                                        tone={catalogAuditPresentation.badgeStatus === 'running' ? 'warning' : 'success'}
                                    >
                                        {catalogAuditPresentation.label}
                                    </StatusBadge>
                                </span>
                            )}
                            {experiment.includeOptions && <Badge variant="info">Options Tracking</Badge>}
                            {experiment.completedAt && (
                                <span className="text-zinc-500 text-sm">
                                    Completed {dayjs(experiment.completedAt).format('MMM D, YYYY h:mm A')}
                                </span>
                            )}
                        </div>
                        {strategyQuery.data?.strategies?.[0] && strategyId && (
                            <div className="mt-2">
                                <span className="text-zinc-400 text-sm mr-2">Strategy:</span>
                                <button
                                    className="text-blue-400 hover:text-blue-300 text-sm transition-colors underline"
                                    onClick={() => navigate(`/user/tradingStrategy/${strategyId}`)}
                                >
                                    {strategyQuery.data.strategies[0].name}
                                </button>
                            </div>
                        )}
                    </div>
                    <div className="flex flex-wrap gap-2 xl:flex-shrink-0 xl:justify-end">
                        <Button
                            className="inline-flex items-center whitespace-nowrap"
                            variant="secondary"
                            onClick={() => navigate(compareExperimentsUrl)}
                        >
                            Compare Many
                        </Button>
                        <Button
                            className="inline-flex items-center whitespace-nowrap"
                            variant="secondary"
                            onClick={() =>
                                navigate(
                                    !Number.isNaN(strategyIdFromQuery) && strategyIdFromQuery > 0
                                        ? `/user/experimentResults/${experimentId}/entities?strategyId=${strategyIdFromQuery}`
                                        : `/user/experimentResults/${experimentId}/entities`
                                )
                            }
                        >
                            View All Entities
                        </Button>
                        <Button
                            className="inline-flex items-center whitespace-nowrap"
                            disabled={durations.length === 0}
                            variant="secondary"
                            onClick={handleExportCSV}
                        >
                            <FaDownload className="mr-2" />
                            {primaryObjective?.mode === 'strategy_exit' ? 'Export Fixed-Horizon CSV' : 'Export CSV'}
                        </Button>
                    </div>
                </div>

                <InputCoverageCallout coverage={experiment.inputCoverage} />

                {/* Experiment Info */}
                <Card className="bg-zinc-900 mb-6">
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                        <div>
                            <div className="text-zinc-400 text-sm mb-1">Date Range</div>
                            <div className="text-white">
                                {dayjs(experiment.dateRange.start).format('MMM D, YYYY')} -{' '}
                                {dayjs(experiment.dateRange.end).format('MMM D, YYYY')}
                            </div>
                        </div>
                        <div>
                            <div className="text-zinc-400 text-sm mb-1">Entities Tested</div>
                            <div className="text-white">{experiment.entities.length} entities</div>
                            <div className="text-zinc-500 text-xs mt-1">
                                {experiment.entities
                                    .slice(0, 3)
                                    .map((e) => e.symbol)
                                    .join(', ')}
                                {experiment.entities.length > 3 && ` +${experiment.entities.length - 3} more`}
                            </div>
                        </div>
                        <div>
                            <div className="text-zinc-400 text-sm mb-1">Durations Tested</div>
                            <div className="text-white">{durations.length} durations</div>
                            <div className="text-zinc-500 text-xs mt-1">
                                {durations.map((d: { duration: string }) => d.duration).join(', ')}
                            </div>
                        </div>
                    </div>
                </Card>

                <ResearchWorkspaceCard
                    experimentId={experimentId}
                    workspace={experiment.lifecycle?.researchWorkspace ?? null}
                    onChanged={() => {
                        void resultsQuery.refetch();
                    }}
                />

                <div className="mb-6 border-b border-zinc-800">
                    <div className="flex flex-wrap gap-2" role="tablist">
                        {experimentViewOptions
                            .filter((option) => !option.adminOnly || canAccessAdminWorkbenches)
                            .map((option) => {
                                const isActive = renderedView === option.id;

                                const disabled =
                                    (option.id === 'discovery' || option.id === 'entryOptimizer') &&
                                    experiment.executionStatus !== 'completed';

                                return (
                                    <button
                                        aria-selected={isActive}
                                        className={`relative -mb-px inline-flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
                                            isActive
                                                ? 'border-blue-500 text-blue-300'
                                                : 'border-transparent text-zinc-400 hover:border-zinc-600 hover:text-zinc-100'
                                        } ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                                        disabled={disabled}
                                        key={option.id}
                                        role="tab"
                                        type="button"
                                        onClick={() => setExperimentView(option.id)}
                                    >
                                        {option.id === 'discovery' && <FaChartBar size={14} />}
                                        {option.label}
                                        {option.id === 'discovery' && discoveryJobCount.active > 0 && (
                                            <span className="rounded-full bg-blue-500 px-1.5 py-0.5 text-xs text-white motion-safe:animate-pulse">
                                                {discoveryJobCount.active}
                                            </span>
                                        )}
                                        {option.id === 'discovery' && discoveryJobCount.total > 0 && discoveryJobCount.active === 0 && (
                                            <span className="rounded-full bg-green-600 px-1.5 py-0.5 text-xs text-white">
                                                {discoveryJobCount.total}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                    </div>
                </div>

                {isResolvingWorkbenchScope ? (
                    <Card className="bg-zinc-900">
                        <DataLoadingSpinner text="Loading workbench scope..." />
                    </Card>
                ) : renderedView === 'regime' ? (
                    <RegimeProfilePanel
                        key={experimentId}
                        durations={durations}
                        executionStatus={experiment.executionStatus}
                        experimentId={experimentId}
                        strategy={strategyQuery.data?.strategies?.[0] ?? null}
                        strategyId={strategyId}
                        includeOptions={Boolean(experiment.includeOptions)}
                        primaryExitCandidateId={primaryObjective?.mode === 'strategy_exit' ? primaryObjective.exitCandidateId : null}
                        initialDuration={requestedWorkbenchDuration ?? (durations.length === 1 ? durations[0].duration : undefined)}
                        initialHorizonSeconds={requestedWorkbenchHorizon ?? null}
                    />
                ) : renderedView === 'discovery' ? (
                    <ParameterDiscovery
                        autoOpenAnalysis={hasRequestedWorkbenchScope}
                        discoveryStatus={experiment.discoveryStatus}
                        durations={durations}
                        executionStatus={experiment.executionStatus}
                        experimentId={experimentId}
                        initialDuration={selectedWorkbenchDuration}
                        initialHorizonSeconds={selectedWorkbenchHorizon}
                        initialMetric={selectedWorkbenchMetric}
                        materializationEntityType={realizedOutcomeMaterializationEntityType}
                        strategyId={strategyId}
                        onScopeChange={handleDiscoveryScopeChange}
                    />
                ) : renderedView === 'scorecards' ? (
                    canAccessAdminWorkbenches && selectedWorkbenchDuration && selectedWorkbenchHorizon ? (
                        <>
                            <Card className="bg-zinc-900 mb-6">
                                <div className="grid gap-4 md:grid-cols-3">
                                    <FormSelect
                                        noMargin
                                        label="Duration"
                                        options={durations.map((duration) => ({
                                            value: duration.duration,
                                            label: duration.duration
                                        }))}
                                        value={selectedWorkbenchDuration}
                                        onChange={(event) => updateWorkbenchScope({ duration: event.target.value })}
                                    />
                                    <FormSelect
                                        noMargin
                                        label="Time Horizon"
                                        options={
                                            durations
                                                .find((duration) => duration.duration === selectedWorkbenchDuration)
                                                ?.horizons.map((horizon) => ({
                                                    value: String(horizon),
                                                    label: formatTimeHorizon(horizon)
                                                })) ?? []
                                        }
                                        value={String(selectedWorkbenchHorizon)}
                                        onChange={(event) => updateWorkbenchScope({ horizonSeconds: Number(event.target.value) })}
                                    />
                                    <FormSelect
                                        noMargin
                                        label="Metric"
                                        options={[
                                            { value: 'stock', label: 'Stock' },
                                            { value: 'options', label: 'Options', disabled: !experiment.includeOptions }
                                        ]}
                                        value={selectedWorkbenchMetric}
                                        onChange={(event) => handleWorkbenchMetricChange(event.target.value as ParameterDiscoveryMetric)}
                                    />
                                </div>
                            </Card>
                            <ParameterDiscoveryAuditPanel
                                duration={selectedWorkbenchDuration}
                                enabled={experiment.executionStatus === 'completed'}
                                experimentId={experimentId}
                                hasOptionMetricData={experiment.includeOptions}
                                horizonSeconds={selectedWorkbenchHorizon}
                                materializationEntityType={realizedOutcomeMaterializationEntityType}
                                metric={selectedWorkbenchMetric}
                                mode="scorecards"
                                onMetricChange={handleWorkbenchMetricChange}
                                strategyId={strategyId}
                            />
                        </>
                    ) : (
                        <Card className="bg-zinc-900">
                            <p className="text-zinc-400">
                                Scorecard workbench is available after parameter discovery data has been generated.
                            </p>
                        </Card>
                    )
                ) : renderedView === 'optimizer' ? (
                    <ScreenerOptimizerPanel
                        durations={durations}
                        entities={experiment.entities.map((e) => ({
                            ...e,
                            entityType: requireOrdinaryEntityType(e.entityType)
                        }))}
                        executionStatus={experiment.executionStatus}
                        experimentId={experimentId}
                        experimentName={experiment.name}
                        strategyId={strategyId}
                    />
                ) : renderedView === 'entryOptimizer' ? (
                    <Card className="bg-zinc-900">
                        <div className="text-sm font-medium text-zinc-100">Entry Optimizer</div>
                        <p className="mt-2 max-w-3xl text-sm text-zinc-400">
                            Tune the strategy&apos;s Trade Logic V2 entry thresholds — and auto-discover additive gates its events expose —
                            by running one relaxed envelope over this experiment&apos;s proven universe, then sweeping every candidate
                            analytically. This experiment&apos;s entities, durations and date window become the study&apos;s dataset
                            configuration.
                        </p>
                        <div className="mt-4 flex gap-2">
                            <Button
                                disabled={strategyId === null}
                                to={
                                    strategyId !== null
                                        ? `/user/entryOptimizer/new?strategyId=${strategyId}&sourceExperimentId=${experimentId}`
                                        : undefined
                                }
                            >
                                New Study From This Universe
                            </Button>
                            <Button to="/user/entryOptimizer" variant="outline-dark">
                                Open Entry Optimizer
                            </Button>
                        </div>
                    </Card>
                ) : renderedView === 'admin' ? (
                    selectedWorkbenchDuration && selectedWorkbenchHorizon ? (
                        <>
                            <Card className="bg-zinc-900 mb-6">
                                <div className="grid gap-4 md:grid-cols-3">
                                    <FormSelect
                                        noMargin
                                        label="Duration"
                                        options={durations.map((duration) => ({
                                            value: duration.duration,
                                            label: duration.duration
                                        }))}
                                        value={selectedWorkbenchDuration}
                                        onChange={(event) => updateWorkbenchScope({ duration: event.target.value })}
                                    />
                                    <FormSelect
                                        noMargin
                                        label="Time Horizon"
                                        options={
                                            durations
                                                .find((duration) => duration.duration === selectedWorkbenchDuration)
                                                ?.horizons.map((horizon) => ({
                                                    value: String(horizon),
                                                    label: formatTimeHorizon(horizon)
                                                })) ?? []
                                        }
                                        value={String(selectedWorkbenchHorizon)}
                                        onChange={(event) => updateWorkbenchScope({ horizonSeconds: Number(event.target.value) })}
                                    />
                                    <FormSelect
                                        noMargin
                                        label="Metric"
                                        options={[
                                            { value: 'stock', label: 'Stock' },
                                            { value: 'options', label: 'Options', disabled: !experiment.includeOptions }
                                        ]}
                                        value={selectedWorkbenchMetric}
                                        onChange={(event) => handleWorkbenchMetricChange(event.target.value as ParameterDiscoveryMetric)}
                                    />
                                </div>
                            </Card>
                            <ParameterDiscoveryAuditPanel
                                duration={selectedWorkbenchDuration}
                                enabled={experiment.executionStatus === 'completed'}
                                experimentId={experimentId}
                                hasOptionMetricData={experiment.includeOptions}
                                horizonSeconds={selectedWorkbenchHorizon}
                                materializationEntityType={realizedOutcomeMaterializationEntityType}
                                metric={selectedWorkbenchMetric}
                                mode="admin"
                                onMetricChange={handleWorkbenchMetricChange}
                                strategyId={strategyId}
                            />
                        </>
                    ) : (
                        <Card className="bg-zinc-900">
                            <p className="text-zinc-400">Admin workbench is available after parameter discovery data has been generated.</p>
                        </Card>
                    )
                ) : (
                    /* Duration Results */
                    <div className="space-y-6">
                        {primaryObjective && (
                            <PrimaryObjectivePerformanceCard
                                automaticParameterDiscoveryEnabled={experiment.discoveryStatus !== 'disabled'}
                                objective={primaryObjective}
                            />
                        )}

                        <Card className="bg-zinc-900" bodyClassName="space-y-1">
                            <div className="flex flex-wrap items-center gap-2">
                                <h2 className="text-lg font-semibold text-white">
                                    {primaryObjective?.mode === 'strategy_exit' ? 'Fixed-horizon proxy' : 'Fixed-horizon results'}
                                </h2>
                                {primaryObjective?.mode === 'strategy_exit' && <Badge variant="secondary">Secondary</Badge>}
                            </div>
                            <p className="text-sm text-zinc-400">
                                {primaryObjective?.mode === 'strategy_exit'
                                    ? `These marks value each entry at the configured horizons. They do not apply global exit candidate #${primaryObjective.exitCandidateId} and are not the primary objective.`
                                    : 'These results value each entry at the configured horizons.'}
                            </p>
                        </Card>

                        {durations.length === 0 ? (
                            <Card className="bg-zinc-900">
                                <EmptyState
                                    description={
                                        experiment.executionStatus === 'running' || experiment.executionStatus === 'queued'
                                            ? 'The experiment is still running. Results will appear as they are calculated.'
                                            : undefined
                                    }
                                    title="No results available yet."
                                />
                            </Card>
                        ) : (
                            durations.map((durationResult) => {
                                // Calculate overall metrics for this duration
                                let totalProfitSum = 0;
                                let totalWinSum = 0;
                                let totalEntryCount = 0;

                                durationResult.horizons.forEach((horizon) => {
                                    const metrics = durationResult.metrics[String(horizon)];

                                    if (metrics && metrics.totalEntries > 0) {
                                        totalProfitSum += (metrics.avgProfitPercent || 0) * metrics.totalEntries;
                                        totalWinSum += metrics.profitableEntries;
                                        totalEntryCount += metrics.totalEntries;
                                    }
                                });

                                const overallAvgProfit = totalEntryCount > 0 ? totalProfitSum / totalEntryCount : null;
                                const overallWinRate = totalEntryCount > 0 ? (totalWinSum / totalEntryCount) * 100 : null;

                                // Prepare data for the detail table
                                const tableData = durationResult.horizons.map((horizon) => ({
                                    horizonSeconds: horizon,
                                    horizonLabel: formatTimeHorizon(horizon),
                                    sourceEntryCount: durationResult.totalEntries,
                                    metrics: durationResult.metrics[String(horizon)] || {
                                        avgProfitPercent: null,
                                        avgSpyProfitPercent: null,
                                        avgAlphaPercent: null,
                                        avgEntryEdgePercent: null,
                                        entryEdgeCoveredCount: 0,
                                        entryEdgeCoveredEmptyCount: 0,
                                        entryEdgeMissingCount: 0,
                                        winRate: null,
                                        winRateVsSpy: null,
                                        avgMaxProfitPercent: null,
                                        avgMaxLossPercent: null,
                                        profitableEntries: 0,
                                        totalEntries: 0,
                                        medianProfitPercent: null,
                                        medianAlphaPercent: null,
                                        stdDevProfitPercent: null,
                                        profitFactor: null,
                                        coefficientOfVariation: null,
                                        optionAvgProfitPercent: null,
                                        avgOptionEntryEdgePercent: null,
                                        optionEntryEdgeCoveredCount: 0,
                                        optionEntryEdgeCoveredEmptyCount: 0,
                                        optionEntryEdgeMissingCount: 0,
                                        optionWinRate: null,
                                        optionAvgMaxProfitPercent: null,
                                        optionAvgMaxLossPercent: null,
                                        optionProfitableEntries: 0,
                                        optionTotalEntries: 0,
                                        medianOptionProfitPercent: null,
                                        optionOutperformanceRate: null,
                                        optionAvgLeverage: null,
                                        optionSuccessRate: null
                                    }
                                }));

                                return (
                                    <Card className="bg-zinc-900" key={durationResult.duration}>
                                        {/* Duration Header */}
                                        <div className="flex items-center justify-between -m-4 p-4 rounded-lg">
                                            <div className="flex items-center gap-6">
                                                <div>
                                                    <h3 className="text-white font-semibold text-lg">{durationResult.duration} Duration</h3>
                                                    <div className="text-zinc-400 text-sm mt-1">
                                                        {durationResult.totalEntries} entries across {durationResult.totalEntities} entities
                                                    </div>
                                                </div>

                                                {/* Summary Metrics */}
                                                <div className="flex gap-8 ml-8">
                                                    <div>
                                                        <div className="text-zinc-500 text-xs">Fixed-Horizon Avg</div>
                                                        <div
                                                            className={`text-lg font-semibold ${overallAvgProfit !== null && overallAvgProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}
                                                        >
                                                            {overallAvgProfit !== null ? `${overallAvgProfit.toFixed(2)}%` : '-'}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className="text-zinc-500 text-xs">Fixed-Horizon Win Rate</div>
                                                        <div className="text-lg font-semibold text-white">
                                                            {overallWinRate !== null ? `${overallWinRate.toFixed(1)}%` : '-'}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className="text-zinc-500 text-xs">Time Horizons</div>
                                                        <div className="text-lg font-semibold text-white">
                                                            {durationResult.horizons.length}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex-shrink-0">
                                                <Button
                                                    size="sm"
                                                    variant="secondary"
                                                    onClick={() =>
                                                        navigate(
                                                            `/user/experimentResults/${experimentId}/entities?duration=${durationResult.duration}${preservedStrategyQueryPart}`
                                                        )
                                                    }
                                                >
                                                    View by Entity
                                                </Button>
                                            </div>
                                        </div>

                                        {/* Detail Table */}
                                        <div className="mt-6 pt-6 border-t border-zinc-800">
                                            <div className="mb-3 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                                                <div className="text-sm text-zinc-500">
                                                    Showing {durationColumnPreset === 'all' ? 'all' : durationColumnPreset} metrics
                                                </div>
                                                <FormSelect
                                                    noMargin
                                                    className="min-w-[160px]"
                                                    fullWidth={false}
                                                    label="Columns"
                                                    options={durationColumnPresetOptions.map((option) => ({
                                                        ...option,
                                                        disabled: option.value === 'options' && !experiment.includeOptions
                                                    }))}
                                                    size="sm"
                                                    value={durationColumnPreset}
                                                    onChange={(event) =>
                                                        setDurationColumnPreset(event.target.value as DurationColumnPreset)
                                                    }
                                                />
                                            </div>
                                            <DataTable
                                                allowOverflow
                                                striped
                                                columns={buildDurationColumns(experiment?.includeOptions || false, durationColumnPreset)}
                                                data={tableData}
                                                pagination={{ enabled: false, pageSize: 100 }}
                                                search={false}
                                            />
                                        </div>
                                    </Card>
                                );
                            })
                        )}
                    </div>
                )}
            </div>
        </UserLayout>
    );
};

export default ExperimentResults;
